import React from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import {
  body16MainStyles,
  smallcapsRegular13Styles,
} from "@constants/typography"
import AnimatedDash, {
  Container as AnimatedDashContainer,
  animationStyles,
} from "@components/buttons/common/AnimatedDash"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import ProductTag, {
  ProductTagProps,
  Container as ProductTagContainer,
} from "@components/ProductTag"
import { MobileOnly, TabletAndDesktopOnly } from "@utils/MediaQuery"
import { getProductURL } from "@utils/urls"

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  ${ProductTagContainer} {
    margin-left: 5.84px;
    margin-bottom: 4.32px;
  }
`

export const ImageWrapper = styled.div<{
  hoverType?: string
  paddingTop?: boolean
}>`
  position: relative;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  display: flex;
  padding: 16px;
  transition: background-color ${animation.medium} ${cubicBezier};
  background-color: ${colors.backgroundGray};

  .productBlock__LifestyleImage,
  .productBlock__GalleryImage {
    display: none;
  }

  @media ${breakpoints.laptop} {
    align-self: stretch;
    justify-self: stretch;
    padding: 20px;
    padding-top: ${({ paddingTop }) => (paddingTop ? "48px" : "20px")};

    .productBlock__LifestyleImage,
    .productBlock__GalleryImage {
      position: absolute;
      left: 0;
      bottom: 0;
      display: initial;
      visibility: hidden;

      img {
        align-self: stretch;
        justify-self: stretch;
        padding: 20px;
      }
    }

    &:hover {
      ${({ hoverType }) => {
        switch (hoverType) {
          case "productBlock__LifestyleImage":
            return `
              .productBlock__BaseImage {
                visibility: hidden;
              }
              
              .productBlock__LifestyleImage {
                visibility: visible !important;
              }
            `
          case "productBlock__GalleryImage":
            return `
              .productBlock__BaseImage {
                visibility: hidden;
              }
              
              .productBlock__GalleryImage {
                visibility: visible !important;
              }
            `
          default:
            return `
              background-color: ${colors.backgroundGrayHover};
            `
        }
      }}
    }
  }

  ${ImageHelperContainer} {
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
    height: auto;

    img {
      object-fit: contain;
    }
  }
`

const Tags = styled.div`
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  display: flex;
  margin-top: auto;
`

const Content = styled.div`
  width: 100%;
  height: auto;
  min-height: 126px;
  padding-top: 14px;
  background: transparent;

  @media ${breakpoints.laptop} {
    min-height: 102px;
  }

  @media ${breakpoints.largeDesktop} {
    min-height: 82px;
  }
`

const Row = styled.div`
  display: flex;
  margin-bottom: 4px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  ${AnimatedDashContainer} {
    margin-left: 12px;
    margin-right: 2px;
    flex-shrink: 0;
  }
`

const Title = styled.div`
  ${body16MainStyles}
`

const Subtitle = styled.div`
  ${smallcapsRegular13Styles}
  display: inline-block;
  opacity: 0.3;
`

const DecoratedSubtitle = styled(Subtitle)`
  margin-right: 12px;
  text-decoration: line-through;
`

const compactStyles = `
  ${ImageWrapper} {
    background-color: transparent;
  }

  ${Container} {
    height: 100%;
  }

  ${Content} {
    padding: 18px 16px 20px;
  }
`

const BlockContainer = styled.div<{ compactVersion?: boolean }>`
  ${animationStyles}

  @media ${breakpoints.mobileOnly} {
    ${({ compactVersion }) => (compactVersion ? compactStyles : "")}
  }

  @media ${breakpoints.laptop} {
    flex-grow: 1;
    display: flex;
    height: 100%;
    max-height: calc(100% - 38px);

    &:hover {
      cursor: pointer;
    }
  }
`

const GatsbyLink = styled(({ compactVersion, ...props }) => (
  <Link {...props} />
))<any>`
  ${({ to }) => (!to.length ? "pointer-events: none" : "cursor: pointer")};
  ${animationStyles}

  @media ${breakpoints.mobileOnly} {
    ${({ compactVersion }) => (compactVersion ? compactStyles : "")}
  }

  @media ${breakpoints.laptop} {
    flex-grow: 1;
    display: flex;
    height: 100%;
    max-height: calc(100% - 38px);
  }
`

interface ProductBlockProps {
  selectedDeviceSlug?: string
  customPrint?: boolean
  sku?: string
  slug?: string
  tags?: Array<ProductTagProps>
  image: string
  lifestyleImage?: string
  galleryImage?: string
  title: string
  subtitle: string
  decoratedSubtitle?: string
  hideDashOnMobile?: boolean
  compactVersion?: boolean
  paddingTop?: boolean
  clickOnly?: boolean
  onClick?: any
}

const ProductBlock: React.FC<ProductBlockProps> = ({
  selectedDeviceSlug,
  customPrint,
  sku,
  slug,
  tags,
  image,
  lifestyleImage,
  galleryImage,
  title,
  subtitle,
  decoratedSubtitle,
  hideDashOnMobile,
  compactVersion,
  paddingTop,
  clickOnly,
  onClick,
}) => {
  const Block = (
    <Container>
      <ImageWrapper
        hoverType={
          !!lifestyleImage
            ? "productBlock__LifestyleImage"
            : !!galleryImage
            ? "productBlock__GalleryImage"
            : undefined
        }
        paddingTop={paddingTop}
      >
        {tags && tags.length > 0 && (
          <Tags>
            {tags.map((tag) => (
              <ProductTag key={tag.text} {...tag} />
            ))}
          </Tags>
        )}
        <ImageHelper className="productBlock__BaseImage" src={image} />
        {lifestyleImage && (
          <ImageHelper
            className="productBlock__LifestyleImage"
            src={lifestyleImage!}
          />
        )}
        {galleryImage && (
          <ImageHelper
            className="productBlock__GalleryImage"
            src={galleryImage!}
          />
        )}
      </ImageWrapper>
      <Content>
        <Row>
          <Title>{title}</Title>
          <MobileOnly>
            {!hideDashOnMobile && !compactVersion && <AnimatedDash />}
          </MobileOnly>
          <TabletAndDesktopOnly>
            <AnimatedDash />
          </TabletAndDesktopOnly>
        </Row>
        {decoratedSubtitle && (
          <DecoratedSubtitle>{decoratedSubtitle}</DecoratedSubtitle>
        )}
        <Subtitle>{subtitle}</Subtitle>
      </Content>
    </Container>
  )

  let productLink

  if (customPrint && selectedDeviceSlug && slug) {
    productLink = getProductURL(slug, sku)
  } else if (slug) {
    productLink = getProductURL(slug)
  }

  return image ? (
    clickOnly ? (
      <BlockContainer
        onClick={(e) => {
          e.stopPropagation()
          onClick && onClick()
        }}
        compactVersion={compactVersion}
      >
        {Block}
      </BlockContainer>
    ) : (
      <GatsbyLink
        to={productLink}
        onClick={onClick}
        compactVersion={compactVersion}
      >
        {Block}
      </GatsbyLink>
    )
  ) : null
}

export default ProductBlock
